import { Alert, Button, Flex } from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import {
  useSimeTbServiceGetSimeTbPersonByIdnpEndpoint,
  useSimeTbServiceGetSimeTbTreatmentPlanListEndpoint
} from 'src/api/queries'
import { Permissions, TreatmentPlanStatus2 } from 'src/api/requests'
import { Text } from 'src/components'
import { useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

type Props = {
  personId?: string
  idnp?: string | null
}

/** The TB treatment scheme in progress in SIME TB for this person, shown in
 *  SIME HIV (patient page, consultation). Renders nothing when the user cannot
 *  see SIME TB or the person is not registered there. */
export const ActiveSimeTbTreatmentCard = ({ personId, idnp }: Props) => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()
  const { hasPermission } = useUser()

  const canViewSimeTb = hasPermission(
    Permissions.INTEGRATIONS_SIME_TB_PERSONS_VIEW
  )

  const { data: simeTbPerson } = useSimeTbServiceGetSimeTbPersonByIdnpEndpoint(
    { idnp: String(idnp) },
    [String(idnp)],
    { enabled: canViewSimeTb && !!idnp, retry: false }
  )

  const { data: plans } = useSimeTbServiceGetSimeTbTreatmentPlanListEndpoint(
    { personId: simeTbPerson?.id, pageNumber: 1, pageSize: 50 },
    ['active', simeTbPerson?.id],
    { enabled: canViewSimeTb && !!simeTbPerson?.id }
  )

  if (!canViewSimeTb || !simeTbPerson?.id || !plans) return null

  const activePlan = (plans.data ?? [])
    .filter((plan) => plan.status === TreatmentPlanStatus2.IN_PROGRESS)
    .sort(
      (a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()
    )[0]

  if (!activePlan) {
    return (
      <Alert
        type="info"
        showIcon
        message={t('tb_screening.active_scheme_title')}
        description={t('tb_screening.active_scheme_none')}
      />
    )
  }

  return (
    <Alert
      type="warning"
      showIcon
      message={t('tb_screening.active_scheme_title')}
      description={
        <Flex wrap="wrap" align="center" gap={16}>
          <Text weight={400}>
            {t('tb_screening.active_scheme_regimen')}:{' '}
            <Text weight={700}>
              {activePlan.translation?.[currentLang.value] ?? '-'}
            </Text>
          </Text>
          <Text weight={400}>
            {t('tb_screening.active_scheme_start')}:{' '}
            <Text weight={700}>
              {activePlan.startDate
                ? dayjs(activePlan.startDate).format(dayMonthYearFormatWithDot)
                : '-'}
            </Text>
          </Text>
          <Button
            size="small"
            onClick={() =>
              navigate(
                paths.patients.simeTB.details(
                  personId,
                  simeTbPerson.id,
                  activePlan.id
                )
              )
            }
          >
            {t('common.details')}
          </Button>
        </Flex>
      }
    />
  )
}
